import Image from "next/image"
import { useRouter } from "next/router"
import { AiOutlineCheckCircle } from "react-icons/ai"
import stateCityDictionary, {
  formatName,
  urlifyName,
} from "../../constants/stateCityDictionary"
import * as config from "../../util/config"
import { buildUrl } from "../../util/string"
import cityNeedForTransport from "../../constants/pet-shipping/city-need-for-transport.json"
import { Anchor } from "src/app/components/anchor"
import { safelyRenderHtml } from "../../util/safelyRenderHtml"
interface CityTransportInfo {
  city: string
  state: string
  data: string
  services: string
  costOfShipping?: string
  methodOfTransport?: string
  timingAndDistance?: string
  vipService?: string
}

export default function PetShippingCost() {
  const router = useRouter()
  const { city, state } = router.query
  const formattedState = formatName(state as string)
  const urlifiedCity = city ? urlifyName(city as string) : ""

  const originalCity =
    stateCityDictionary[formattedState]?.cities.find(
      (dictCity) => urlifyName(dictCity) === urlifiedCity,
    ) ?? ""

  const formattedCity = originalCity ? formatName(originalCity) : ""
  const stateAbbrev = stateCityDictionary[formattedState]?.abbreviation ?? ""
  const locationCityState = formattedCity
    ? `${formattedCity}, ${formattedState}`
    : formattedState

  const locationName = formattedCity
    ? `${formattedCity}, ${stateAbbrev}`
    : formattedState
  const appHost = config.getAppHost()

  // Find city-specific transport information
  const cityInfo = cityNeedForTransport.find(
    (info) => info.city === urlifiedCity,
  ) as CityTransportInfo | undefined

  return (
    <section className="w-full my-8 xl:my-16 flex xl:gap-20 md:gap-10">
      <article className="md:pl-8">
        <h3 className="mb-5 text-blue7 text-3xl font-montserrat font-medium">
          What determines the cost of pet shipping in {locationCityState}?
        </h3>
        <div className="mb-8 text-xl">
          {safelyRenderHtml(
            cityInfo?.costOfShipping ||
              "It's hard to give an exact estimate of the cost of pet transportation because of the variables:",
          )}
        </div>
        <section className="ml-16 md:ml-0 relative max-w-prose-3xl">
          <h4 className="absolute -left-16 text-6xl text-blue7/20 font-bold font-montserrat">
            1
          </h4>
          <h3 className="text-2xl font-montserrat font-semibold">
            Method of Transport
          </h3>
          {cityInfo?.methodOfTransport ? (
            <div className="text-xl">
              {safelyRenderHtml(cityInfo.methodOfTransport)}
            </div>
          ) : (
            <>
              <p className="text-xl">
                First, you'll decide between air or pet ground transportation.
                Small animals can be checked as carry-on luggage on flights,
                which is the least expensive option — keep in mind you'll need
                to buy a ticket for your pet as well as yourself. Larger breeds
                must fly in the cargo area, which can cost as much as $1,000 or
                more depending on the pet shipping airline's policy.
              </p>
              <p className="text-xl">
                Save money on{" "}
                <Anchor href={buildUrl(appHost, "/shipments/new/animals")}>
                  {formattedCity} pet transport{" "}
                </Anchor>
                service with the CitizenShipper marketplace. Drivers bid on your
                transportation listing, lowering costs. Once you receive bids,
                read reviews and communicate directly with them to find the
                perfect match.
              </p>
            </>
          )}
        </section>
        <section className="ml-16 md:ml-0 relative max-w-prose-3xl">
          <h4 className="absolute -left-16 text-6xl text-blue7/20 font-bold font-montserrat">
            2
          </h4>
          <h3 className="text-2xl font-montserrat font-semibold">
            Timing and Distance
          </h3>
          {cityInfo?.timingAndDistance ? (
            <div className="text-xl">
              {safelyRenderHtml(cityInfo.timingAndDistance)}
            </div>
          ) : (
            <>
              <p className="text-xl">
                {formattedCity} pet transport companies charge a per-mile rate
                for ground transport; the further the distance, the lower your
                pet transport cost per mile.
              </p>
              <p className="text-xl">
                The timing of the transport matters. Because summer and holidays
                are the busiest times of the year, pet shipping companies in{" "}
                {locationCityState} charge a premium due to limited
                availability.
              </p>
            </>
          )}
        </section>
        <section className="ml-16 md:ml-0 relative max-w-prose-3xl">
          <h4 className="absolute -left-16 text-6xl text-blue7/20 font-bold font-montserrat">
            3
          </h4>
          <h3 className="text-2xl font-montserrat font-semibold">
            VIP Service
          </h3>
          {cityInfo?.vipService ? (
            <div className="text-xl">
              {safelyRenderHtml(cityInfo.vipService)}
            </div>
          ) : (
            <>
              <p className="text-xl">
                You can expect to pay extra for VIP service. Some transport
                companies in {locationName} transport your pet alone in a pet
                taxi. Why is VIP service preferential?
              </p>
              <ul className="p-0">
                <li className="flex">
                  <AiOutlineCheckCircle className="h-8 w-8 min-w-[2rem] mr-5 fill-green5" />
                  <p className="grow text-xl">
                    VIP service maintains quarantine for young pets who aren't
                    fully vaccinated.
                  </p>
                </li>
                <li className="flex">
                  <AiOutlineCheckCircle className="h-8 w-8 min-w-[2rem] mr-5 fill-green5" />
                  <p className="grow text-xl">
                    The transporter's sole focus is your pet, ensuring it
                    travels stress-free.
                  </p>
                </li>
                <li className="flex">
                  <AiOutlineCheckCircle className="h-8 w-8 min-w-[2rem] mr-5 fill-green5" />
                  <p className="grow text-xl">
                    You won't need to stress about the behavior of temperamental
                    dogs or cats.
                  </p>
                </li>
              </ul>
            </>
          )}
        </section>
      </article>
      <Image
        className="hidden lg:block mx-auto object-contain"
        src="/images/mobile-device.webp"
        alt={`pet transportation service ${city ? city + ", " : ""} ${state}`}
        width={300}
        height={668}
      />
    </section>
  )
}
